import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';

interface DocumentUploadDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  acceptedFileTypes?: string[];
  maxFileSize?: number; // in bytes
  maxFiles?: number;
}

const DEFAULT_MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const configuredMaxFileSize = Number(process.env.REACT_APP_MAX_FILE_SIZE);
const defaultMaxFileSize = Number.isFinite(configuredMaxFileSize) && configuredMaxFileSize > 0
  ? configuredMaxFileSize
  : DEFAULT_MAX_FILE_SIZE_BYTES;

const DocumentUploadDropzone: React.FC<DocumentUploadDropzoneProps> = ({
  onFilesSelected,
  acceptedFileTypes = ['.pdf', '.docx', '.xlsx', '.txt', '.zip'],
  maxFileSize = defaultMaxFileSize,
  maxFiles = 10,
}) => {
  const { t } = useTranslation();
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFiles = useCallback((files: File[]): { validFiles: File[]; errors: string[] } => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    if (files.length > maxFiles) {
      errors.push(t('upload.maxFilesError', { count: maxFiles }));
      return { validFiles: [], errors };
    }

    files.forEach((file) => {
      // Check file size
      if (file.size > maxFileSize) {
        errors.push(t('upload.fileSizeError', {
          name: file.name,
          size: Math.round(maxFileSize / 1024 / 1024)
        }));
        return;
      }

      // Check file type
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!acceptedFileTypes.includes(fileExtension)) {
        errors.push(t('upload.fileTypeError', {
          name: file.name,
          types: acceptedFileTypes.join(', ')
        }));
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, errors };
  }, [maxFiles, maxFileSize, acceptedFileTypes, t]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    setError(null);

    const files = Array.from(e.dataTransfer.files);
    const { validFiles, errors } = validateFiles(files);

    if (errors.length > 0) {
      setError(errors.join('; '));
      return;
    }

    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }
  }, [onFilesSelected, validateFiles]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const handleClick = () => {
    // This will be handled by the parent component's file input
  };

  return (
    <Box>
      <Paper
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          backgroundColor: isDragActive ? 'primary.light' : 'background.paper',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'grey.50',
          },
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CloudUploadIcon
          sx={{
            fontSize: 64,
            color: isDragActive ? 'primary.main' : 'grey.400',
            mb: 2,
          }}
        />

        <Typography
          variant="h6"
          sx={{
            color: isDragActive ? 'primary.main' : 'text.primary',
            mb: 1,
          }}
        >
          {isDragActive ? t('upload.dropFilesHere') : t('upload.dragDropFiles')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('upload.orClickToBrowse')}
        </Typography>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" justifyContent="center">
          {acceptedFileTypes.map((type) => (
            <Box key={type} display="flex" alignItems="center" gap={0.5}>
              <DocumentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {type.toUpperCase()}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {t('upload.maxSize', { size: `${Math.round(maxFileSize / 1024 / 1024)}MB` })} • {t('upload.maxFiles', { count: maxFiles })}
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default DocumentUploadDropzone;
